<template>
    <div id="app" :class="companySkin">
        <v-app>
            <div class="main-content">
                <CompanyHeader />
                <AuthProvider>
                    <router-view />
                    <DevPanel v-if="showDevPanel" />
                    <DebugOverlay v-if="showOverlayDebug" />
                </AuthProvider>
            </div>

            <Modals />

            <Footer
                v-if="showFooter && market"
                class="main-footer"
                :showActions="showFooterActions"
                :market="market"
                logoTheme="inverted"
                footerTheme="primary" />
        </v-app>
    </div>
</template>

<script lang="ts">
    import { defineComponent, computed } from 'vue';
    import { useStore } from './store';
    import { useRoute } from 'vue-router';
    import { configBase } from '@/config/base';
    import { isStenaCompany } from '@shared/config/market/market.helpers';
    import { feature } from '@libs/utils/feature/feature';
    import { Market } from '@shared/config/market/market.models';
    import Modals from './components/modals/Modals.vue';
    import Footer from '@ui/components/Layout/Footer/Footer.vue';
    import DevPanel from '@/components/shared/DevPanel/DevPanel.vue';
    import CompanyHeader from '@/components/shared/CompanyHeader/CompanyHeader.vue';
    import AuthProvider from './providers/AuthProvider.vue';
    import DebugOverlay from '@/components/shared/DebugOverlay/DebugOverlay.vue';

    export default defineComponent({
        name: 'App',
        components: {
            DebugOverlay,
            DevPanel,
            Modals,
            Footer,
            CompanyHeader,
            AuthProvider,
        },
        head() {
            const store = useStore();

            const market = computed((): Market => store.getters['general/getMarket']);

            const lang = market.value?.locale ?? 'en';
            const title = market.value?.name ?? '';
            const favIcon = market.value?.assets.favIcon ?? '';

            return {
                title,
                link: [{ rel: 'icon', href: favIcon }],
                htmlAttrs: {
                    lang,
                },
            };
        },

        setup() {
            const store = useStore();
            const route = useRoute();

            const market = computed((): Market => store.getters['general/getMarket']);

            const showDevPanel = computed(() => configBase.devMode.enabled);

            const showOverlayDebug = feature('debug-overlay');

            const companySkin = computed(() =>
                isStenaCompany(market.value?.number) ? 'stena-app' : 'sembo-app'
            );

            const showFooterActions = computed(() => {
                const whitelist = ['booking'];
                const name = route?.name ?? '';
                return whitelist.includes(name.toString());
            });

            const showFooter = computed(() => {
                const blackList = ['redirecting'];
                const name = route?.name ?? '';
                return !blackList.includes(name.toString());
            });

            return {
                market,
                companySkin,
                showFooter,
                showFooterActions,
                showDevPanel,
                showOverlayDebug,
            };
        },
    });
</script>

<style lang="scss">
    @forward './styles/layout.scss';
</style>
